(() => {
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  const PRODUCT_SELECTOR = '#MainContent[data-template="product"] .product.grid--2-col-tablet';
  const PANEL_SELECTOR = '.product__info-container.product__column-sticky';
  const GALLERY_SELECTOR = '.oylin-product-gallery';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class OylinProductSmartRail {
    constructor(product) {
      this.product = product;
      this.panel = product.querySelector(PANEL_SELECTOR);
      this.wrapper = this.panel?.closest('.product__info-wrapper');
      this.gallery = product.querySelector(GALLERY_SELECTOR);
      this.resizeObserver = null;
      this.frame = 0;
      this.onResize = this.queueMeasure.bind(this);
      this.onWheel = this.handleWheel.bind(this);

      if (!this.panel || !this.wrapper || !this.gallery) return;

      this.product.dataset.oylinSmartRail = 'true';
      this.product.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueMeasure());
        this.resizeObserver.observe(this.gallery);
        this.resizeObserver.observe(this.panel);
      }

      this.measure();
    }

    getHeaderOffset() {
      const rootStyle = getComputedStyle(document.documentElement);
      const cssHeaderHeight = parseFloat(rootStyle.getPropertyValue('--header-height')) || 0;
      const header =
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper') ||
        document.querySelector('header');

      const measuredHeader = header?.getBoundingClientRect().height || 0;
      return Math.max(cssHeaderHeight, measuredHeader) + 16;
    }

    queueMeasure() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.measure());
    }

    reset() {
      this.product.classList.remove('oylin-smart-rail-active');
      this.wrapper.style.removeProperty('--oylin-smart-rail-section-height');
      this.panel.style.removeProperty('--oylin-smart-rail-top');
      this.panel.style.removeProperty('--oylin-smart-rail-height');
      this.panel.scrollTop = 0;
    }

    measure() {
      if (!DESKTOP_QUERY.matches) {
        this.reset();
        return;
      }

      const galleryHeight = Math.ceil(this.gallery.getBoundingClientRect().height);
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const top = this.getHeaderOffset();
      const bottomGap = 20;
      const availableViewport = Math.max(320, viewportHeight - top - bottomGap);
      const railHeight = Math.max(320, Math.min(galleryHeight, availableViewport));

      if (!galleryHeight) return;

      this.wrapper.style.setProperty('--oylin-smart-rail-section-height', `${galleryHeight}px`);
      this.panel.style.setProperty('--oylin-smart-rail-top', `${Math.round(top)}px`);
      this.panel.style.setProperty('--oylin-smart-rail-height', `${Math.round(railHeight)}px`);
      this.product.classList.add('oylin-smart-rail-active');

      const maxScroll = Math.max(0, this.panel.scrollHeight - this.panel.clientHeight);
      this.panel.scrollTop = clamp(this.panel.scrollTop, 0, maxScroll);
    }

    handleWheel(event) {
      if (!DESKTOP_QUERY.matches || !this.product.classList.contains('oylin-smart-rail-active')) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey) return;

      const maxScroll = this.panel.scrollHeight - this.panel.clientHeight;
      if (maxScroll <= 2) return;

      const delta = event.deltaY;
      if (Math.abs(delta) < 0.5) return;

      const productRect = this.product.getBoundingClientRect();
      const top = parseFloat(this.panel.style.getPropertyValue('--oylin-smart-rail-top')) || 0;
      const isInRailPhase =
        productRect.top <= top + 2 &&
        productRect.bottom > Math.min(window.innerHeight, top + this.panel.clientHeight + 2);

      if (!isInRailPhase) return;

      const goingDown = delta > 0;
      const goingUp = delta < 0;
      const canScrollDown = this.panel.scrollTop < maxScroll - 1;
      const canScrollUp = this.panel.scrollTop > 1;

      if ((goingDown && canScrollDown) || (goingUp && canScrollUp)) {
        event.preventDefault();
        this.panel.scrollTop = clamp(this.panel.scrollTop + delta, 0, maxScroll);
      }
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      this.product.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('resize', this.onResize);
      window.visualViewport?.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
      this.reset();
    }
  }

  const instances = new WeakMap();

  const init = (root = document) => {
    root.querySelectorAll?.(PRODUCT_SELECTOR).forEach((product) => {
      if (instances.has(product)) return;
      const instance = new OylinProductSmartRail(product);
      instances.set(product, instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();